import axios, {AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse} from 'axios';

abstract class HttpClient {
  protected readonly instance: AxiosInstance;
  private isRefreshing = false;
  private refreshSubscribers: (() => void)[] = [];

  protected constructor() {
    this.instance = axios.create({});
    this.initializeResponseInterceptor();
  }

  private initializeResponseInterceptor = () => {
    this.instance.interceptors.response.use(
      this.handleResponse,
      this.handleResponseError,
    );
  };

  private handleResponse = ({ data }: AxiosResponse) => data;

  private handleResponseError = async (error: AxiosError): Promise<any> => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry && originalRequest.url !== '/api/refresh-token') {
      originalRequest._retry = true;

      if (this.isRefreshing) {
        return new Promise(resolve => {
          this.refreshSubscribers.push(() => {
            resolve(this.instance(originalRequest));
          });
        });
      }

      this.isRefreshing = true;

      try {
        await this.refreshAccessToken();
        this.isRefreshing = false;
        this.refreshSubscribers.forEach(cb => cb());
        this.refreshSubscribers = [];

        return this.instance(originalRequest);
      } catch (refreshError) {
        this.isRefreshing = false;
        this.refreshSubscribers = [];
        window.localStorage.setItem('refreshTokenExpired', 'true');
        setTimeout(() => window.location.reload(), 2000);
      }
    }

    return this.handleError(error);
  };

  public async refreshAccessToken(): Promise<any> {
    return await this.instance.post('/api/refresh-token');
  }

  private handleError = async (error: unknown): Promise<never> => {
    return Promise.reject(error);
  };

  public publicDoGet(url: string) : Promise<any> {
    return this.instance.get(url);
  }

  public doGet(url: string) : Promise<any> {
    return this.instance.get(url);
  }

  public doPost(url: string, data?: object, contentType?: string, onUploadProgress?: any) : Promise<any> {
    return this.instance.post(
      url,
      data ?? {},
      {
        headers: {
          ContentType: contentType ?? 'application/json',
        },
        onUploadProgress
      }
    );
  }

  public doPut(url: string, data?: object, contentType?: string, onUploadProgress?: any) : Promise<any> {
    return this.instance.put(
      url,
      data ?? {},
      {
        headers: {
          ContentType: contentType ?? 'application/json',
        },
        onUploadProgress
      }
    );
  }

  protected doDelete(url: string) : Promise<any> {
    return this.instance.delete(url);
  }
}

export default HttpClient;
